import React, {forwardRef, FunctionComponent, Ref, useCallback} from 'react'
import styles from './TaskBody.module.sass'
import Results from '../../../../../data-components/Results/Results'
import UserPanel from '../../../../../data-components/elements/UserElement/UserElement'
import {PartialTask, Task} from '../../../../../../responses/tasks'
import TaskStatusPicker from '../TaskStatusPicker/TaskStatusPicker'
import PriorityPicker from '../../../../../data-components/pickers/PriorityPicker/PriorityPicker'
import Button from '../../../../../controls/Button/Button'
import {useDispatch} from 'react-redux'
import {Dispatch} from 'redux'
import {RootActions} from '../../../../../../redux/root'
import {PERMISSIONS} from '../../../../../../constants/permissions'
import {Link} from 'react-router-dom'
import {urlWithParams} from '../../../../../../http/utils'
import {ROUTES} from '../../../../../../http/routes'
import {TaskStatus} from '../../../../../../constants/enums'
import UserPicker from '../../../../../data-components/pickers/UserPicker/UserPicker'
import {ID} from '../../../../../../types/UtilityTypes'
import {taskActions} from '../../../../../../redux/data/task/task-actions'
import {motion} from 'framer-motion'
import {apiRequests} from '../../../../../../http/config'
import {fromQueryWithReset, ResetSearch} from '../../../../../../utils/rx-operators'
import {galleryActions} from '../../../../../../redux/global/gallery/gallery-actions'
import EditIcon from 'files/edit.svg'
import TagIcon from 'files/tag/tag-add.svg'
import DeleteIcon from 'files/delete.svg'
import ChevronRightIcon from 'files/chevron/right.svg'
import {useAllPermissions, useRootSelector} from '../../../../../../utils/hooks'
import {entityElem} from '../../../../../../utils/entity-utils'
import Menu from '../../../../../etc/Menu/Menu'
import {useQueries} from '../../../../../../utils/hooks/useQueries'
import {FileData} from '../../../../../../responses/files'
import {lineSelector} from '../../../../../../utils/selectors'
import DeadlinePicker from '../../../../../data-components/date/DeadlinePicker'
import {HasAllPermissions} from '../../../../../etc/HasPermission'
import {useObservableState} from 'observable-hooks'
import {Observable, of} from 'rxjs'
import {switchMap} from 'rxjs/operators'

interface Props {
    task: Task,
    projectId: ID,
    hideLink?: boolean,
    onUpdate: (changes: PartialTask) => void,
}

const TaskBody: FunctionComponent<Props> = forwardRef((props, ref: Ref<HTMLDivElement>) => {
    const dispatch = useDispatch<Dispatch<RootActions>>()
    const currentUser = useRootSelector(state => state.global.user.data)
    const line = useRootSelector(lineSelector(props.task.parent_id || props.task.id))
    const canEdit = useAllPermissions([PERMISSIONS.TASK.EDIT])
    const canOpen = useAllPermissions([PERMISSIONS.TASK.OPEN])
    const canClose = useAllPermissions([PERMISSIONS.TASK.CLOSE])

    const {isMobile} = useQueries()

    const onEdit = useCallback(() => dispatch(taskActions.startUpdate(props.task)), [
        dispatch, props.task
    ])
    const waybill = useCallback(() => dispatch(taskActions.waybill.request({id: props.task.id})), [
        dispatch, props.task.id
    ])
    const onDelete = useCallback(() => dispatch(taskActions.startDelete({
        message: 'Подтвердите удаление задачи',
        task: props.task,
    })), [dispatch, props.task])

    const openGallery = useCallback((current: FileData) => dispatch(galleryActions.open({
        type: 'task',
        objectId: props.task.project_work_object_id,
        tasksId: line,
        current,
    })), [line, dispatch, props.task.project_work_object_id])


    const getUsers = (data: Observable<{ query: ResetSearch, taskTypeID?: ID }>) => data.pipe(
        switchMap(({query, taskTypeID}) => of(query).pipe(
            fromQueryWithReset(query =>
                apiRequests.project.targetUsersNewTask({
                    params: {projectId: props.projectId}, query: {...query, task_type: taskTypeID}
                })
            ))
        ))
    //console.log(props.task.type.id)
    const [users, onUserSearch] = useObservableState(getUsers, [])

    /*const [users, onUserSearch] = useObservableState<User[], {search: string, reset: boolean}>(fromQueryWithReset(query => apiRequests.project.targetUsersEditTask({
        params: { projectId: props.projectId, taskId: props.task.id },
        query
    })), [])*/


    return <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1, position: 'static'}}
        exit={{opacity: 0, position: 'absolute', top: 0}}
        ref={ref}
        className={styles.taskBody}
    >
        <Results
            showEmpty
            className={styles.results}
            mode={'line'}
            parentId={props.task.parent_id || props.task.id}
            onClick={openGallery}
        />
        <div className={styles.user}>
            {
                props.task.type.id === 16 && <Button
                    onClick={waybill}
                    title={'Скачать бирку'}
                ><TagIcon/></Button>
            }

            <UserPicker
                className={styles.picker}
                data={users}
                text={'Исполнитель'}
                title={'Изменить исполнителя'}
                disabled={
                    !(canEdit
                        || props.task.target_user?.id == currentUser?.id
                        || entityElem(currentUser, props.task.users)
                    )
                    || props.task.status_id === TaskStatus.CLOSED}
                user={props.task.target_user}
                onClick={target_user => props.onUpdate({target_user})}
                onChange={(search, reset) => {
                    onUserSearch({query: {search, reset}, taskTypeID: props.task.type.id})
                }}
            />
        </div>
        {
            (!isMobile && props.task.users.length > 0) && <div className={styles.users}>
                {
                    props.task.users.map(user => <UserPanel
                        key={user.id}
                        disabled
                        onlyIcon
                        user={user}
                    />)
                }
            </div>
        }
        <TaskStatusPicker
            className={styles.status}
            task={props.task}
            disabled={isMobile}
            inactive={!canClose}
            onChange={(task, comment) => props.onUpdate({...task, comment})}
        />
        {
            !isMobile && (props.task.status_id !== TaskStatus.CLOSED) && <DeadlinePicker
                value={props.task.deadline_at}
                deadline_text={props.task.deadline}
                deadline_type={props.task.deadline_type}
                onChange={deadline_at => props.onUpdate({deadline_at})}
            />
        }
        {
            props.task.status_id === TaskStatus.OPENED && <PriorityPicker
                className={styles.priority}
                disabled={!canOpen}
                priority={props.task.priority_id}
                onChange={priority_id => props.onUpdate({priority_id})}
            />
        }
        {
            !isMobile ? <>
                {
                    (canEdit && props.task.status_id === TaskStatus.OPENED) && <Button
                        className={styles.edit}
                        title={'Редактировать'}
                        onClick={onEdit}
                    >
                        <EditIcon/>
                    </Button>
                }
                <HasAllPermissions permissions={[PERMISSIONS.TASK.DELETE]}>
                    <Button
                        className={styles.remove}
                        title={'Удалить'}
                        onClick={onDelete}
                    >
                        <DeleteIcon/>
                    </Button>
                </HasAllPermissions>
            </> : <Menu
                className={styles.menu}
                items={[
                    {
                        icon: <EditIcon/>,
                        text: 'Редактировать',
                        onClick: onEdit,
                    },
                    {
                        icon: <DeleteIcon/>,
                        text: 'Удалить',
                        onClick: onDelete,
                    }
                ]}
            />
        }
        {
            !props.hideLink && <Link
                className={styles.link}
                to={urlWithParams({taskId: props.task.id})(ROUTES.TASK)}
            >
                <Button>
                    <ChevronRightIcon/>
                    {isMobile ? 'Перейти' : 'Перейти к задаче'}
                </Button>
            </Link>
        }
    </motion.div>
})

TaskBody.displayName = 'TaskBody'

export default TaskBody